export const ACS_ED_ATTAIN_RACE_UNIVERSE_LABEL =
  'population ages 25 and older in the ACS educational-attainment-by-race table';

export type SexWorkplaceFacts = {
  latestYear: number | null;
  femaleWorkers: number | null;
  maleWorkers: number | null;
  femaleShare: number | null;
  displayGeographyName: string;
  geographyCoverageSentence: string;
  trendSentence: string;
};

export type RaceCompositionFacts = {
  latestYear: number | null;
  populationUniverseLabel: string;
  largestRaceLabel: string | null;
  largestRaceShare: number | null;
  secondRaceSentence: string;
  thirdRaceSentence: string;
  largestShareChangeSentence: string;
};

function parseWorkerCount(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function buildSexWorkplaceFacts(params: {
  locationName: string;
  sexSeries: { year: number; sex: string; total_worker: number | null }[];
  isUsingCountyData: boolean;
  countyName: string | null;
}): SexWorkplaceFacts {
  const { locationName, sexSeries, isUsingCountyData, countyName } = params;
  if (!sexSeries.length) {
    return {
      latestYear: null,
      femaleWorkers: null,
      maleWorkers: null,
      femaleShare: null,
      displayGeographyName: locationName,
      geographyCoverageSentence: '',
      trendSentence: '',
    };
  }
  const latestYear = Math.max(...sexSeries.map((r) => r.year));
  const latest = sexSeries.filter((r) => r.year === latestYear);
  const femaleWorkers = latest
    .filter((r) => /^(female|f)$/i.test(r.sex.trim()))
    .reduce((s, r) => s + (parseWorkerCount(r.total_worker) ?? 0), 0);
  const maleWorkers = latest
    .filter((r) => /^(male|m)$/i.test(r.sex.trim()))
    .reduce((s, r) => s + (parseWorkerCount(r.total_worker) ?? 0), 0);
  const denom = femaleWorkers + maleWorkers;
  const femaleShare = denom > 0 ? (femaleWorkers / denom) * 100 : null;
  const displayGeographyName =
    isUsingCountyData && countyName ? countyName : locationName;
  const geographyCoverageSentence =
    isUsingCountyData && countyName
      ? ` Data are shown for ${countyName} because workplace employment for the selected place was not available in LODES.`
      : '';
  return {
    latestYear,
    femaleWorkers: femaleWorkers || null,
    maleWorkers: maleWorkers || null,
    femaleShare,
    displayGeographyName,
    geographyCoverageSentence,
    trendSentence: '',
  };
}

export function buildRaceCompositionFacts(params: {
  locationName: string;
  compositionSeries: { year: number; race: string; count: number }[];
  chartShareByRaceYear: Map<string, number>;
  raceLabelMap: Record<string, string>;
}): RaceCompositionFacts {
  const { locationName, compositionSeries, chartShareByRaceYear, raceLabelMap } = params;
  if (!compositionSeries.length) {
    return {
      latestYear: null,
      populationUniverseLabel: ACS_ED_ATTAIN_RACE_UNIVERSE_LABEL,
      largestRaceLabel: null,
      largestRaceShare: null,
      secondRaceSentence: '',
      thirdRaceSentence: '',
      largestShareChangeSentence: '',
    };
  }
  const latestYear = Math.max(...compositionSeries.map((r) => r.year));
  const latestRows = compositionSeries.filter((r) => r.year === latestYear);
  const total = latestRows.reduce((s, r) => s + (Number.isFinite(r.count) ? r.count : 0), 0);
  const shares = latestRows
    .map((r) => ({
      race: r.race,
      label: raceLabelMap[r.race] || r.race,
      share: total > 0 ? (r.count / total) * 100 : null,
    }))
    .filter((x) => x.share != null)
    .sort((a, b) => (b.share ?? 0) - (a.share ?? 0));

  const largest = shares[0];
  const second = shares[1];
  const third = shares[2];

  let largestShareChangeSentence = '';
  const earliestYear = Math.min(...compositionSeries.map((r) => r.year));
  if (earliestYear < latestYear && largest) {
    const earlyKey = `${earliestYear}|${largest.race}`;
    const lateKey = `${latestYear}|${largest.race}`;
    const earlyShare = chartShareByRaceYear.get(earlyKey);
    const lateShare = chartShareByRaceYear.get(lateKey);
    if (earlyShare != null && lateShare != null) {
      const pp = lateShare - earlyShare;
      largestShareChangeSentence = ` The share for ${largest.label} changed by ${pp >= 0 ? '+' : ''}${pp.toFixed(1)} percentage points from ${earliestYear} to ${latestYear}.`;
    }
  }

  return {
    latestYear,
    populationUniverseLabel: ACS_ED_ATTAIN_RACE_UNIVERSE_LABEL,
    largestRaceLabel: largest?.label ?? null,
    largestRaceShare: largest?.share ?? null,
    secondRaceSentence: second
      ? ` ${second.label} was the second-largest category at ${second.share!.toFixed(1)}%.`
      : '',
    thirdRaceSentence: third
      ? ` ${third.label} represented ${third.share!.toFixed(1)}%.`
      : '',
    largestShareChangeSentence,
  };
}
